import styled, { css } from "styled-components";
import { duration, easing, zIndex } from "@magicbi/ui-library";
import { NAV_PIN_MS } from "../molecules/NavDrawer/NavDrawer.constants";
import type { ShellVariant } from "./Shell.types";

/**
 * Secondary column width is provided per-route via `--shell-secondary-width`
 * CSS variable so the grid can stay 100% declarative.
 *  - KG: sets `auto` → column sizes to the sidebar's intrinsic 220px.
 *  - Connector detail: sets `20%` → instance sidebar fills its grid cell.
 */
const SHELL_GRID = {
  default: css`
    grid-template-columns: var(--shell-sidebar-width, max-content) minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "header header"
      "sidebar main";
  `,
  withBreadcrumb: css`
    grid-template-columns: var(--shell-sidebar-width, max-content) minmax(0, 1fr);
    grid-template-rows: auto auto 1fr;
    grid-template-areas:
      "header header"
      "sidebar pageheader"
      "sidebar main";
  `,
  withSecondary: css`
    grid-template-columns:
      var(--shell-sidebar-width, max-content) var(--shell-secondary-width, auto) minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "header header header"
      "sidebar secondary main";
  `,
  withBreadcrumbAndSecondary: css`
    grid-template-columns:
      var(--shell-sidebar-width, max-content) var(--shell-secondary-width, auto) minmax(0, 1fr);
    grid-template-rows: auto auto 1fr;
    grid-template-areas:
      "header header header"
      "sidebar pageheader pageheader"
      "sidebar secondary main";
  `,
};

export const ShellContainer = styled.div<{
  $variant: ShellVariant;
  $secondaryWidth: string;
  /** Drawer navigation: the shelf column width (0 collapsed/preview, drawer width pinned). */
  $sidebarWidth?: string;
}>`
  display: grid;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.background.secondary},
    ${({ theme }) => theme.colors.background.tertiary}
  );
  box-sizing: border-box;
  --shell-secondary-width: ${({ $secondaryWidth }) => $secondaryWidth};
  ${({ $sidebarWidth }) =>
    $sidebarWidth
      ? css`
          --shell-sidebar-width: ${$sidebarWidth};
          /* Pin / unpin: the content column resizes, the panel itself never moves. */
          transition: grid-template-columns ${NAV_PIN_MS}ms ${easing.slideIn};
          @media (prefers-reduced-motion: reduce) {
            transition-duration: ${duration.instant};
          }
        `
      : ""}
  ${({ $variant }) => SHELL_GRID[$variant]}
`;

export const HeaderSlot = styled.div`
  grid-area: header;
  min-width: 0;
`;

export const SidebarSlot = styled.div<{ $overlay?: boolean }>`
  grid-area: sidebar;
  min-width: 0;
  min-height: 0;
  overflow-y: ${({ $overlay }) => ($overlay ? "visible" : "auto")};
  position: ${({ $overlay }) => ($overlay ? "relative" : "static")};
  /* The open drawer covers the page, including page titles that set their own z-index. */
  z-index: ${({ $overlay }) => ($overlay ? zIndex.overlay : "auto")};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const BreadcrumbSlot = styled.div`
  grid-area: pageheader;
  min-width: 0;
`;

export const SecondarySlot = styled.div`
  grid-area: secondary;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  /* Preserve the established 10px gutter using the shared 4px token scale. */
  padding: calc(
    ${({ theme }) => theme.spacing.base[2]} +
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MainSlot = styled.main`
  grid-area: main;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;
